import { lazy, Suspense, useMemo, useState } from "react";
import { SOLID_PRESETS, SOLID_SHAPES, SolidSpec, MODEL_LIMITS, type Hotspot, type SolidShape } from "@jange/shared";
import { useQuery } from "@tanstack/react-query";
import { Boxes } from "lucide-react";
import { api, type Asset } from "../lib/api";
import { Drawer, Field } from "./ui";
import { BankBrowser } from "./BankBrowser";
import { fmtBytes } from "../lib/upload";

// Même composant que dans l'app : l'enseignant place ses points sur le vrai rendu.
const Model3D = lazy(() => import("@jange/lesson-player/model3d"));

type Spec = SolidSpec;
const num = (s: string, fallback: number) => { const v = Number(s); return s.trim() === "" || !Number.isFinite(v) ? fallback : v; };

/** Solide généré : forme, dimensions fixes ou pilotées par un curseur, options d'affichage. */
export function SolidEditor({ value, lang, onChange }: { value: unknown; lang: string; onChange: (s: Spec) => void }) {
  const raw = (value && typeof value === "object" && (value as { kind?: string }).kind === "solid" ? value : SOLID_PRESETS[0].spec) as Spec;
  const check = useMemo(() => SolidSpec.safeParse(raw), [raw]);
  const set = (p: Partial<Spec>) => onChange({ ...raw, ...p });
  const dims = SOLID_SHAPES[raw.shape].dims as readonly string[];

  const setShape = (shape: SolidShape) => {
    // On garde les dimensions de même nom, on crée les autres en valeur fixe.
    const next: Spec["dims"] = {};
    for (const d of SOLID_SHAPES[shape].dims) next[d] = raw.dims[d] ?? 3;
    const used = new Set(Object.values(next).flatMap((v) => (typeof v === "object" ? [v.slider] : [])));
    set({ shape, dims: next, sliders: raw.sliders.filter((s) => used.has(s.name)) });
  };
  const toggleSlider = (d: string, on: boolean) => {
    if (on) {
      const fixed = typeof raw.dims[d] === "number" ? raw.dims[d] as number : 3;
      set({ dims: { ...raw.dims, [d]: { slider: d } }, sliders: [...raw.sliders.filter((s) => s.name !== d), { name: d, label: { fr: `Dimension ${d}` }, min: 0.5, max: Math.max(fixed * 2, 4), step: 0.5, initial: fixed }] });
    } else {
      const sl = raw.sliders.find((s) => s.name === d);
      set({ dims: { ...raw.dims, [d]: sl?.initial ?? 3 }, sliders: raw.sliders.filter((s) => s.name !== d) });
    }
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ flexWrap: "wrap" }}>
        <span className="muted small" style={{ fontWeight: 700 }}>Modèles :</span>
        {SOLID_PRESETS.map((p) => <button key={p.id} type="button" className="btn sm ghost" onClick={() => onChange(structuredClone(p.spec))}>{p.label}</button>)}
      </div>
      <div className="row" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
        <Field label="Solide">
          <select className="select" value={raw.shape} onChange={(e) => setShape(e.target.value as SolidShape)}>
            {(Object.keys(SOLID_SHAPES) as SolidShape[]).map((k) => <option key={k} value={k}>{SOLID_SHAPES[k].label}</option>)}
          </select>
        </Field>
        <Field label="Unité"><input className="input" style={{ width: 70 }} maxLength={6} value={raw.unit} onChange={(e) => set({ unit: e.target.value })} /></Field>
        <span className="muted small" style={{ height: 36, display: "flex", alignItems: "center" }}>Volume : {SOLID_SHAPES[raw.shape].volume}</span>
      </div>
      <table className="t">
        <thead><tr><th>Dimension</th><th>Curseur</th><th>Valeur / min</th><th>Max</th><th>Pas</th><th>Libellé ({lang})</th></tr></thead>
        <tbody>
          {dims.map((d) => {
            const v = raw.dims[d];
            const sl = typeof v === "object" ? raw.sliders.find((s) => s.name === v.slider) : undefined;
            const setSl = (p: Partial<NonNullable<typeof sl>>) => set({ sliders: raw.sliders.map((s) => (s === sl ? { ...s, ...p } : s)) });
            return (
              <tr key={d}>
                <td className="mono"><b>{d}</b></td>
                <td><input type="checkbox" checked={!!sl} onChange={(e) => toggleSlider(d, e.target.checked)} aria-label={`${d} suit un curseur`} /></td>
                <td>{sl
                  ? <span className="row small">de <input className="input" type="number" style={{ width: 70 }} value={sl.min} onChange={(e) => setSl({ min: num(e.target.value, sl.min) })} /> départ <input className="input" type="number" style={{ width: 70 }} value={sl.initial} onChange={(e) => setSl({ initial: num(e.target.value, sl.initial) })} /></span>
                  : <input className="input" type="number" style={{ width: 80 }} min={0.1} value={typeof v === "number" ? v : 3} onChange={(e) => set({ dims: { ...raw.dims, [d]: num(e.target.value, 3) } })} />}</td>
                <td>{sl && <input className="input" type="number" style={{ width: 70 }} value={sl.max} onChange={(e) => setSl({ max: num(e.target.value, sl.max) })} />}</td>
                <td>{sl && <input className="input" type="number" style={{ width: 70 }} step={0.1} value={sl.step} onChange={(e) => setSl({ step: num(e.target.value, sl.step) })} />}</td>
                <td>{sl && <input className="input" value={sl.label[lang] ?? ""} placeholder={sl.label.fr} maxLength={60} onChange={(e) => setSl({ label: { ...sl.label, [lang]: e.target.value } })} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row small" style={{ flexWrap: "wrap", fontWeight: 600 }}>
        {([["edges", "Arêtes"], ["height", "Hauteur en pointillés"], ["volume", "Volume calculé en direct"]] as const).map(([k, l]) => (
          <label key={k} className="row"><input type="checkbox" checked={raw.show[k]} onChange={(e) => set({ show: { ...raw.show, [k]: e.target.checked } })} /> {l}</label>
        ))}
      </div>
      {!check.success && <div className="alert err small">{check.error.issues[0]?.message ?? "Solide invalide"}</div>}
    </div>
  );
}

type PartLabel = { id: string; label: Record<string, string>; text?: Record<string, string>; hidden?: boolean };
type ModelBlock = { assetId: string; fallback2dAssetId: string | null; sizeBytes: number; hotspots: Hotspot[]; parts: PartLabel[] };

/** Modèle GLB : choix dans la médiathèque, image de secours, points annotés placés d'un clic sur le modèle. */
export function ModelEditor({ block, lang, models, images, onChange }: { block: ModelBlock; lang: string; models: Asset[]; images: Asset[]; onChange: (b: Partial<ModelBlock>) => void }) {
  const [placing, setPlacing] = useState(false);
  const [picking, setPicking] = useState(false);
  const bankItem = useQuery({ queryKey: ["bank-item", block.assetId], queryFn: () => api.bankItem(block.assetId), enabled: !!block.assetId, staleTime: 60_000, retry: false });
  const [selected, setSelected] = useState<string | null>(null);
  const model = models.find((m) => m.id === block.assetId);
  const modelUrl = model?.url ?? bankItem.data?.url ?? null;
  const meta = model?.meta as { triangles?: number; nodes?: string[] } | null | undefined;
  // Parties du modèle (légende automatique) : connues par la banque, couleurs lues au chargement de la 3D.
  const [partColors, setPartColors] = useState<Record<string, string>>({});
  const partIds = Object.keys(partColors).length ? Object.keys(partColors) : bankItem.data?.parts ?? (meta?.nodes ?? []).filter((n) => n !== "modèle");
  const setHotspot = (id: string, p: Partial<Hotspot>) => onChange({ hotspots: block.hotspots.map((h) => (h.id === id ? { ...h, ...p } : h)) });

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "2 1 260px" }}>
          <div className="model-pick">
            {bankItem.data?.thumbUrl ? <img src={bankItem.data.thumbUrl} alt="" /> : <span className="ph"><Boxes size={20} /></span>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{model?.title ?? bankItem.data?.title ?? (block.assetId ? "Modèle indisponible" : "Aucun modèle choisi")}</b>
              <span className="tiny muted">{bankItem.data?.credit ?? (block.assetId ? fmtBytes(block.sizeBytes) : "Choisissez-le dans la banque 3D")}</span>
            </div>
            <button type="button" className="btn sm ghost" onClick={() => setPicking(true)}>{block.assetId ? "Changer" : "Choisir"}</button>
          </div>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <Field label="Image de secours (téléphones sans 3D)">
            <select className="select" value={block.fallback2dAssetId ?? ""} onChange={(e) => onChange({ fallback2dAssetId: e.target.value || null })}>
              <option value="">Aucune</option>
              {images.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
          </Field>
        </div>
      </div>
      {meta?.triangles !== undefined && <span className="muted small">{meta.triangles.toLocaleString("fr-FR")} triangles (max {MODEL_LIMITS.maxTriangles.toLocaleString("fr-FR")}){meta.nodes?.length ? ` · parties : ${meta.nodes.slice(0, 8).join(", ")}${meta.nodes.length > 8 ? "…" : ""}` : ""}</span>}

      {modelUrl && (
        <Suspense fallback={<div className="muted small">Chargement du modèle…</div>}>
          <div className="row small" style={{ fontWeight: 600 }}>
            <button type="button" className={`btn sm ${placing ? "amber" : "ghost"}`} disabled={block.hotspots.length >= MODEL_LIMITS.maxHotspots} onClick={() => setPlacing((p) => !p)}>
              {placing ? "Cliquez sur le modèle pour placer le point…" : "+ Placer un point annoté"}
            </button>
            <span className="muted">{block.hotspots.length} / {MODEL_LIMITS.maxHotspots} points</span>
          </div>
          <Model3D src={modelUrl} poster={null} sizeBytes={block.sizeBytes} credit={bankItem.data?.credit ?? null} partLabels={block.parts}
            onParts={(ps) => setPartColors(Object.fromEntries(ps.map((p) => [p.id, p.color])))} hotspots={block.hotspots} lang={lang} autoLoad selected={selected} onHotspot={setSelected}
            onSurfaceClick={placing ? (position, normal) => {
              const id = `p${Date.now().toString(36)}`;
              const round = (v: number[]) => v.map((x) => Math.round(x * 1000) / 1000) as [number, number, number];
              onChange({ hotspots: [...block.hotspots, { id, position: round(position), normal: round(normal), label: { fr: `Point ${block.hotspots.length + 1}` }, text: {} }] });
              setSelected(id);
              setPlacing(false);
            } : undefined} />
        </Suspense>
      )}

      <PartsLegendEditor block={block} lang={lang} parts={partIds} colors={partColors} onChange={(parts) => onChange({ parts })} />

      {block.hotspots.map((h, i) => (
        <div key={h.id} className="row" style={{ alignItems: "flex-start", flexWrap: "wrap", padding: 8, borderRadius: 10, background: h.id === selected ? "var(--amber-tint)" : "var(--surface-2)" }} onClick={() => setSelected(h.id)}>
          <b style={{ width: 24 }}>{i + 1}.</b>
          <div style={{ flex: "1 1 160px" }}><input className="input" placeholder={lang !== "fr" ? h.label.fr : "Nom de la partie"} maxLength={60} value={h.label[lang] ?? ""} onChange={(e) => setHotspot(h.id, { label: { ...h.label, [lang]: e.target.value } })} /></div>
          <div style={{ flex: "2 1 260px" }}><textarea className="textarea" style={{ minHeight: 40 }} placeholder={lang !== "fr" ? h.text.fr ?? "Explication" : "Explication courte"} maxLength={400} value={h.text[lang] ?? ""} onChange={(e) => setHotspot(h.id, { text: { ...h.text, [lang]: e.target.value } })} /></div>
          <button type="button" className="icon-btn" aria-label="Supprimer le point" onClick={(e) => { e.stopPropagation(); onChange({ hotspots: block.hotspots.filter((x) => x.id !== h.id) }); }}>✕</button>
        </div>
      ))}
      {picking && (
        <Drawer title="Banque 3D" wide onClose={() => setPicking(false)} subtitle="Choisissez le modèle de ce bloc">
          <BankBrowser mode="pick" onPick={(m) => { onChange({ assetId: m.id, sizeBytes: m.sizeBytes, hotspots: m.id === block.assetId ? block.hotspots : [] }); setPicking(false); }} />
        </Drawer>
      )}
    </div>
  );
}

/**
 * Légende automatique : chaque partie du modèle porte déjà son nom (celui de la banque).
 * La leçon peut le remplacer ou le traduire, ajouter une courte explication, ou masquer la partie.
 */
function PartsLegendEditor({ block, lang, parts, colors, onChange }: { block: ModelBlock; lang: string; parts: string[]; colors: Record<string, string>; onChange: (p: PartLabel[]) => void }) {
  if (parts.length < 2) return null;
  const byId = new Map(block.parts.map((p) => [p.id, p]));
  const set = (id: string, patch: Partial<PartLabel>) => {
    const cur = byId.get(id) ?? { id, label: {} };
    const next = { ...cur, ...patch };
    const empty = !Object.values(next.label).some(Boolean) && !Object.values(next.text ?? {}).some(Boolean) && !next.hidden;
    onChange(empty ? block.parts.filter((p) => p.id !== id) : [...block.parts.filter((p) => p.id !== id), next]);
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row wrap" style={{ gap: 8 }}>
        <b className="small">Légende automatique</b>
        <span className="tiny muted">L'élève touche une partie pour la voir seule et lire son nom ; « Étiquettes » et « Trouve la partie » sont proposés. Renommez, traduisez, expliquez ou masquez une partie si besoin.</span>
      </div>
      {parts.map((id) => {
        const o = byId.get(id);
        return (
          <div key={id} className="row wrap" style={{ gap: 8, padding: "6px 8px", borderRadius: 10, background: "var(--surface-2)", opacity: o?.hidden ? 0.55 : 1 }}>
            <span style={{ width: 12, height: 12, borderRadius: 99, background: colors[id] ?? "var(--border-strong)", flexShrink: 0 }} aria-hidden />
            <input className="input" style={{ flex: "1 1 180px", minHeight: 32, padding: "4px 8px" }} aria-label={`Nom affiché : ${id}`} placeholder={lang !== "fr" ? (o?.label.fr || id) : id} maxLength={60}
              value={o?.label[lang] ?? ""} onChange={(e) => set(id, { label: { ...(o?.label ?? {}), [lang]: e.target.value } })} />
            <input className="input" style={{ flex: "2 1 240px", minHeight: 32, padding: "4px 8px" }} aria-label={`Explication : ${id}`} placeholder={lang !== "fr" && o?.text?.fr ? o.text.fr : "Explication courte (facultatif)"} maxLength={300}
              value={o?.text?.[lang] ?? ""} onChange={(e) => set(id, { text: { ...(o?.text ?? {}), [lang]: e.target.value } })} />
            <label className="check tiny" title="Retirer cette partie de la légende de la leçon"><input type="checkbox" className="switch" checked={!o?.hidden} onChange={(e) => set(id, { hidden: !e.target.checked || undefined })} aria-label={`Afficher dans la légende : ${id}`} />Afficher</label>
          </div>
        );
      })}
    </div>
  );
}
